import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ChevronRight, RefreshCw, Phone, PhoneCall, Clock, Target, Award,
  TrendingUp, Calendar, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell
} from 'recharts';

// ─── HELPERS ──────────────────────────────────────────────────────────────────

/** Safe numeric parse - handles strings, nulls, undefined */
const safeNum = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? 0 : n;
};

/** Get today's date in IST (YYYY-MM-DD) to match backend which uses Asia/Kolkata */
const getISTDate = () => {
  const now = new Date();
  const istMs = now.getTime() + (5.5 * 60 * 60 * 1000); // UTC + 5:30
  return new Date(istMs).toISOString().split('T')[0];
};

/** Normalize phone number for cross-matching IVR <-> Agent data */
const normalizePhone = (phone) => {
  if (!phone) return '';
  let p = String(phone).trim().replace(/[+\s-]/g, '');
  if (p.startsWith('91') && p.length > 10) p = p.slice(2);
  if (p.startsWith('0') && p.length > 10) p = p.slice(1);
  return p.length >= 10 ? p.slice(-10) : p;
};

/** Format seconds into Xm Ys */
const formatDuration = (seconds) => {
  const s = safeNum(seconds);
  if (s <= 0) return '0s';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

// ─── KPI CARD ─────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, icon: Icon, color = 'text-white', valueColor }) => (
  <div className="flex items-center gap-3 bg-slate-800/60 border border-slate-700/50 rounded-lg px-4 py-3 min-w-[160px]">
    {Icon && (
      <div className={`p-2 rounded-md bg-slate-900/80 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium truncate">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${valueColor || 'text-white'}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function AgentProfile() {
  const { agentId } = useParams();
  const navigate = useNavigate();

  // ── STATE ──
  const [agent, setAgent] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [hourlyActivity, setHourlyActivity] = useState([]);
  const [callLog, setCallLog] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getISTDate());
  const [loading, setLoading] = useState(true);
  const [ivrData, setIvrData] = useState({ callLogs: [], c2cLogs: [] });
  const [callLogLimit, setCallLogLimit] = useState(50);

  // ── DATA FETCHING ──
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Detail is critical — fetch first; historical/hourly are optional
        const detailRes = await axiosInstance.get(`/agent-performance/detail?agent_id=${agentId}&date=${selectedDate}`);
        setAgent(detailRes.data?.data || null);

        // Fetch supplemental data in parallel — failures don't block the page
        const [historicalRes, hourlyRes] = await Promise.allSettled([
          axiosInstance.get(`/agent-performance/historical?agent_id=${agentId}&days=7`),
          axiosInstance.get(`/agent-performance/hourly?agent_id=${agentId}&date=${selectedDate}`),
        ]);

        setHistoricalData(historicalRes.status === 'fulfilled' ? (historicalRes.value.data?.data || []) : []);
        setHourlyActivity(hourlyRes.status === 'fulfilled' ? (hourlyRes.value.data?.data || []) : []);

        // If today, fetch IVR call logs for call log table
        if (selectedDate === getISTDate()) {
          const [callRes, c2cRes] = await Promise.allSettled([
            axiosInstance.get('/ivr/call_logs'),
            axiosInstance.get('/ivr/c2c_logs'),
          ]);
          setIvrData({
            callLogs: callRes.status === 'fulfilled' ? (callRes.value.data?.data || []) : [],
            c2cLogs: c2cRes.status === 'fulfilled' ? (c2cRes.value.data?.data || []) : [],
          });
        } else {
          setIvrData({ callLogs: [], c2cLogs: [] });
        }
      } catch (error) {
        console.error('Failed to fetch agent data:', error);
        toast.error('Failed to load agent profile — check connection and retry');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [agentId, selectedDate, navigate]);

  // ── DERIVED: Call Log from IVR Data ──
  const todaysCallLog = useMemo(() => {
    if (selectedDate !== getISTDate()) return [];

    const logs = [];
    const agentPhone = normalizePhone(agentId);

    // Process call_logs (inbound)
    ivrData.callLogs.forEach(log => {
      const attended = normalizePhone(log.attended_by);
      if (attended === agentPhone) {
        const dur = safeNum(log.call_duration);
        logs.push({
          time: log.call_time,
          number: log.client_no,
          duration: dur,
          direction: 'Inbound',
          status: dur > 0 ? 'Connected' : 'Missed',
          recording_url: log.recording_url,
        });
      }
    });

    // Process c2c_logs (outbound)
    ivrData.c2cLogs.forEach(log => {
      const agentNo = normalizePhone(log.agent_no);
      if (agentNo === agentPhone) {
        const disposition = String(log.disposition || '').toLowerCase();
        logs.push({
          time: log.call_time,
          number: log.client_no,
          duration: safeNum(log.call_duration),
          direction: 'C2C',
          status: disposition === 'answered' ? 'Connected' : 'Missed',
          recording_url: log.recording_url,
        });
      }
    });

    // Sort by time descending
    logs.sort((a, b) => {
      const tA = a.time ? new Date(a.time).getTime() : 0;
      const tB = b.time ? new Date(b.time).getTime() : 0;
      return tB - tA;
    });

    return logs; // Return all calls, limit applied in render
  }, [ivrData, agentId, selectedDate]);

  // ── LOADING STATE ──
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-500 mx-auto mb-2" />
          <div className="text-sm text-slate-400">Loading agent profile...</div>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-slate-400 mb-4">Agent not found or failed to load</div>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => { setLoading(true); setAgent(null); }} variant="outline" className="border-amber-600 text-amber-400 hover:bg-amber-600/10">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
            <Button onClick={() => navigate('/inside-sales')} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Inside Sales
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── RENDER ──

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6">
      {/* Breadcrumb + Date Picker */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span
            onClick={() => navigate('/inside-sales')}
            className="cursor-pointer hover:text-amber-400 transition-colors"
          >
            Inside Sales
          </span>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white font-medium">{agent.name || 'Agent Profile'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={getISTDate()}
            className="w-40 bg-slate-800 border-slate-700 text-slate-100 text-sm"
          />
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <KpiCard
          label="Call Minutes Today"
          value={`${Math.round(safeNum(agent.call_minutes))}m`}
          sub={`Target: 300m`}
          icon={Phone}
          color="text-cyan-400"
          valueColor="text-slate-100"
        />
        <KpiCard
          label="Total Calls"
          value={safeNum(agent.total_calls)}
          sub={`${safeNum(agent.connected_calls)} connected`}
          icon={PhoneCall}
          color="text-purple-400"
          valueColor="text-slate-100"
        />
        <KpiCard
          label="Avg Talk Time"
          value={safeNum(agent.avg_talk_sec) > 0 ? formatDuration(safeNum(agent.avg_talk_sec)) : '--'}
          sub={safeNum(agent.connected_calls) > 0 ? 'per connected call' : 'no connected calls'}
          icon={Clock}
          color="text-blue-400"
          valueColor={safeNum(agent.avg_talk_sec) > 0 ? 'text-slate-100' : 'text-slate-500'}
        />
        <KpiCard
          label="Target %"
          value={`${Math.round((safeNum(agent.call_minutes) / 300) * 100)}%`}
          sub={`${Math.max(0, 300 - Math.round(safeNum(agent.call_minutes)))}m remaining`}
          icon={Target}
          color="text-amber-400"
          valueColor={
            (safeNum(agent.call_minutes) / 300) >= 0.66 ? 'text-emerald-400' :
            (safeNum(agent.call_minutes) / 300) >= 0.33 ? 'text-amber-400' :
            'text-slate-400'
          }
        />
        <KpiCard
          label="Team Rank"
          value={agent?.rank ? `${agent.rank} / ${safeNum(agent.total_agents)}` : `-- / ${safeNum(agent.total_agents)}`}
          sub={`Team avg: ${Math.round(safeNum(agent.team_avg_minutes))}m`}
          icon={Award}
          color="text-emerald-400"
          valueColor={agent?.rank ? 'text-emerald-400' : 'text-slate-500'}
        />
      </div>

      {/* 7-Day Bar Chart */}
      <Card className="bg-slate-900 border-slate-700/50 mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Last 7 Days Performance</CardTitle>
        </CardHeader>
        <CardContent>
          {historicalData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={historicalData} barCategoryGap="30%" barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d) => {
                    try {
                      return new Date(d).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
                    } catch {
                      return d;
                    }
                  }}
                />
                <YAxis
                  stroke="#94a3b8"
                  tick={{ fontSize: 11 }}
                  domain={[0, (dataMax) => Math.max(Math.ceil(dataMax * 1.3), 320)]}
                  tickFormatter={(v) => `${v}m`}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '6px', color: '#e2e8f0' }}
                  formatter={(v) => [`${Math.round(v)}m`, 'Call Minutes']}
                  labelFormatter={(d) => {
                    try { return new Date(d).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }); }
                    catch { return d; }
                  }}
                />
                <ReferenceLine
                  y={300}
                  stroke="#f59e0b"
                  strokeDasharray="5 5"
                  label={{ value: 'Target 300m', fill: '#f59e0b', fontSize: 11, position: 'right' }}
                />
                <Bar dataKey="call_minutes" radius={[6, 6, 0, 0]} maxBarSize={80}>
                  {historicalData.map((entry, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={
                        entry.call_minutes >= 200 ? '#34d399' :
                        entry.call_minutes >= 100 ? '#fbbf24' :
                        '#22d3ee'
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No historical data available yet
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hourly Activity Timeline */}
      <Card className="bg-slate-900 border-slate-700/50 mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Hour-by-Hour Activity Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {(() => {
            const maxMinutes = Math.max(...Array.from({ length: 9 }, (_, i) => {
              const slot = hourlyActivity.find(s => s.hour === (10 + i));
              return safeNum(slot?.minutes);
            }), 1);
            return (
              <>
                <div className="flex gap-2 mb-4">
                  {Array.from({ length: 9 }, (_, i) => {
                    const hour = 10 + i;
                    const slot = hourlyActivity.find(s => s.hour === hour);
                    const minutes = safeNum(slot?.minutes);
                    const hasActivity = minutes > 0;
                    // Intensity: how active relative to best hour (0-1 scale)
                    const intensity = hasActivity ? Math.max(0.3, minutes / maxMinutes) : 0;

                    return (
                      <div
                        key={hour}
                        className={`flex-1 rounded-lg px-3 py-4 text-center border transition-all ${
                          hasActivity
                            ? 'border-cyan-700/60'
                            : 'bg-slate-800/30 border-slate-700/40'
                        }`}
                        style={hasActivity ? {
                          background: `rgba(6, 182, 212, ${intensity * 0.25})`,
                        } : undefined}
                      >
                        <div className="text-xs text-slate-400 mb-1">{hour}:00</div>
                        <div className={`text-lg font-bold tabular-nums ${
                          hasActivity ? 'text-cyan-400' : 'text-slate-600'
                        }`}>
                          {hasActivity ? `${Math.round(minutes * 10) / 10}m` : '--'}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-400">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-cyan-500"></div>
                    <span>Active</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-slate-700"></div>
                    <span>No activity</span>
                  </div>
                </div>
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Today's Call Log */}
      <Card className="bg-slate-900 border-slate-700/50 mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Today's Call Log</CardTitle>
        </CardHeader>
        <CardContent>
          {selectedDate !== getISTDate() ? (
            <div className="text-center py-8 text-slate-500">
              Call log available for today only
            </div>
          ) : todaysCallLog.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No calls logged yet today
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/60 border-b border-slate-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] uppercase text-slate-400">Time</th>
                      <th className="px-3 py-2 text-left text-[10px] uppercase text-slate-400">Number</th>
                      <th className="px-3 py-2 text-left text-[10px] uppercase text-slate-400">Duration</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-slate-400">Direction</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-slate-400">Status</th>
                      <th className="px-3 py-2 text-left text-[10px] uppercase text-slate-400">Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todaysCallLog.slice(0, callLogLimit).map((call, idx) => {
                      const timeStr = (() => {
                        if (!call.time) return null;
                        try {
                          const d = new Date(call.time);
                          if (isNaN(d.getTime())) return null;
                          return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
                        } catch { return null; }
                      })();
                      const phoneNum = normalizePhone(call.number);

                      return (
                        <tr key={idx} className="border-b border-slate-800 hover:bg-slate-800/30">
                          <td className="px-3 py-2 font-mono text-xs">
                            {timeStr
                              ? <span className="text-slate-300">{timeStr}</span>
                              : <span className="text-slate-600 italic">--</span>
                            }
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {phoneNum
                              ? <span className="text-slate-300">{phoneNum}</span>
                              : <span className="text-slate-600 italic">{call.direction === 'Inbound' ? 'IVR Queue' : '--'}</span>
                            }
                          </td>
                          <td className="px-3 py-2 text-slate-300 font-mono text-xs">
                            {formatDuration(call.duration)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              call.direction === 'Inbound' ? 'bg-blue-500/10 text-blue-400' : 'bg-cyan-500/10 text-cyan-400'
                            }`}>
                              {call.direction}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              call.status === 'Connected' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
                            }`}>
                              {call.status}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {call.recording_url ? (
                              <audio
                                controls
                                preload="none"
                                className="h-7 w-full"
                              >
                                <source src={call.recording_url} type="audio/wav" />
                              </audio>
                            ) : (
                              <span className="text-slate-600 text-xs">--</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {todaysCallLog.length > callLogLimit && (
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-slate-500">
                    Showing {Math.min(callLogLimit, todaysCallLog.length)} of {todaysCallLog.length} calls
                  </div>
                  <Button
                    onClick={() => setCallLogLimit(prev => prev + 50)}
                    variant="outline"
                    size="sm"
                    className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  >
                    Show More
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Lead Pipeline Placeholder */}
      <Card className="bg-slate-900 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-lg">Lead Pipeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            <p className="text-sm italic">Coming Soon</p>
            <p className="text-xs mt-2">Lead attribution data integration pending</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
